import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, LogOut, LogIn, Play, RotateCcw, AlertTriangle, ArrowRight, Menu, Lightbulb, Brain, BookmarkPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Story } from '../types';
import { authService } from '../services/auth';
import LoginModal from '../components/LoginModal';
import realCasesService from '../services/realCasesService';
import { getApiUrl } from '../config/apiConfig';
import ScenarioHub from '../scenarios/ScenarioHub';
import { useTheme } from '../contexts/ThemeContext';

const GamePage: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [unlockedStories, setUnlockedStories] = useState<Story[]>([]);
  const [isStartingGame, setIsStartingGame] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [gameMessages, setGameMessages] = useState<any[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isGameWon, setIsGameWon] = useState(false);
  const [isGettingHint, setIsGettingHint] = useState(false);
  const [dailyHintsUsed, setDailyHintsUsed] = useState(0);
  const [storyHintsUsed, setStoryHintsUsed] = useState<{[key: string]: number}>({});
  const [unlockedStoryIds, setUnlockedStoryIds] = useState<string[]>([]);
  const [completedStoryIds, setCompletedStoryIds] = useState<string[]>([]);
  const [finalChapter, setFinalChapter] = useState<Story | null>(null);
  const [showFinalChapter, setShowFinalChapter] = useState(false);
  const [insightStory, setInsightStory] = useState<Story | null>(null);
  const [mainStoryUnlocked, setMainStoryUnlocked] = useState(false);
  const [activeTab, setActiveTab] = useState<'normal' | 'insight' | 'story'>('normal');

  // 提问质量连击 / 奖励提示 / AI复盘 / 知识卡片
  const goodStreakRef = useRef(0);
  const [bonusHintStories, setBonusHintStories] = useState<string[]>([]);
  const [aiReview, setAiReview] = useState('');
  const [isGettingReview, setIsGettingReview] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);
  const [savedCardIds, setSavedCardIds] = useState<string[]>([]);

  // 通用轻提示
  const showToast = (message: string, bgClass: string) => {
    const toast = document.createElement('div');
    toast.className = `fixed top-20 left-1/2 transform -translate-x-1/2 ${bgClass} text-white px-6 py-3 rounded-lg shadow-lg z-50`;
    toast.innerHTML = `<div class="flex items-center">${message}</div>`;
    document.body.appendChild(toast);
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 2500);
  };

  // 计算进度百分比 - 只基于基础故事（不包含最终章等特殊故事）
  const baseStoryCompletedIds = completedStoryIds.filter(id =>
    unlockedStories.some(story => story.id === id)
  );
  const progressPercentage = Math.round((baseStoryCompletedIds.length / unlockedStories.length) * 100) || 0;

  // 检查登录状态
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
    }
  }, []);

  useEffect(() => {
    // 加载现实案例、解锁状态和最终章
    const loadStories = async () => {
      try {
        // 加载已收藏的知识卡片
        setSavedCardIds(
          JSON.parse(localStorage.getItem('lts_knowledge_cards') || '[]').map((c: any) => c.storyId)
        );

        // 加载解锁状态
        const savedProgress = JSON.parse(localStorage.getItem('gameProgress') || '{}');
        const unlockedIds = savedProgress.unlockedStories || [];
        const completedIds = savedProgress.completedStories || [];
        setCompletedStoryIds(completedIds);

        // 加载每题的提示使用次数
        const storyHintsData = localStorage.getItem('storyHintsUsed');
        if (storyHintsData) {
          setStoryHintsUsed(JSON.parse(storyHintsData));
        }

        const casesData = await realCasesService.loadRealCases();
        // 获取所有故事（全部分类：反诈/普法/校园/职场/网络/情感/金融）
        const allStories: Story[] = [];
        for (const [groupCategory, stories] of Object.entries(casesData.turtleSoup)) {
          if (Array.isArray(stories)) {
            allStories.push(...stories.map(story => ({ ...story, category: story.category || groupCategory })));
          }
        }
        setUnlockedStories(allStories);

        // 支持 /game?story=<id> 直达指定题目（今日挑战等入口使用）
        const targetId = new URLSearchParams(window.location.search).get('story');
        const targetStory = targetId ? allStories.find(s => s.id === targetId) : null;
        if (targetStory) {
          setSelectedStory(targetStory);
        }

        // 全量开放普通题目（保留已解锁的特殊剧情ID）
        const baseStoryIds = allStories
          .map(story => story.id)
          .filter((id): id is string => Boolean(id));
        const specialUnlockedIds = unlockedIds.filter(
          (id: string) => !baseStoryIds.includes(id)
        );
        const mergedUnlockedIds = Array.from(new Set([...baseStoryIds, ...specialUnlockedIds]));
        setUnlockedStoryIds(mergedUnlockedIds);
        savedProgress.unlockedStories = mergedUnlockedIds;
        localStorage.setItem('gameProgress', JSON.stringify(savedProgress));

        // 加载最终章
        try {
          const finalResponse = await fetch('/data/finalChapter.json');
          if (finalResponse.ok) {
            const finalData = await finalResponse.json();
            const finalStory: Story = {
              ...finalData.finalChapter,
              category: '最终章'
            };
            setFinalChapter(finalStory);
          }
        } catch (error) {
          console.error('Error loading final chapter:', error);
        }

        // 检查双线故事完成状态，用于解锁顿悟时刻
        const dualStoryProgress = JSON.parse(localStorage.getItem('dualStoryProgress') || '{}');
        const modernCompleted = dualStoryProgress['1']?.completed || false;
        const ancientCompleted = dualStoryProgress['2']?.completed || false;
        const dualStoryCompleted = modernCompleted && ancientCompleted;

        // 如果双线故事完成且最终章未解锁，则解锁最终章
        if (dualStoryCompleted && !savedProgress.unlockedStories.includes('final_chapter')) {
          const updatedUnlockedIds = [...savedProgress.unlockedStories, 'final_chapter'];
          setUnlockedStoryIds(updatedUnlockedIds);
          savedProgress.unlockedStories = updatedUnlockedIds;
          localStorage.setItem('gameProgress', JSON.stringify(savedProgress));
        }

        // 加载核心启示题目
        try {
          const insightResponse = await fetch('/data/insightStory.json');
          if (insightResponse.ok) {
            const insightData = await insightResponse.json();
            const insightStory: Story = {
              ...insightData.insightStory,
              category: '启示类',
              isSpecial: true
            };
            setInsightStory(insightStory);
          }
        } catch (error) {
          console.error('Error loading insight story:', error);
        }
      } catch (error) {
        console.error('Error loading stories:', error);
        // 如果加载失败，显示默认故事
        const defaultStories: Story[] = [
          {
            id: 'fraud_1',
            title: '自证清白的代价',
            surface: '我必须花钱证明自己无罪',
            answer: '冒充公检法诈骗',
            difficulty: 'medium',
            category: '冒充公检法'
          }
        ];
        setUnlockedStories(defaultStories);
      }
    };

    loadStories();
  }, []);

  // 检查是否应该解锁顿悟时刻（王大爷剧情）
  useEffect(() => {
    if (finalChapter && unlockedStories.length > 0) {
      // 检查是否完成了双线叙事
      const dualStoryProgress = localStorage.getItem('dualStoryProgress');
      if (dualStoryProgress) {
        const progress = JSON.parse(dualStoryProgress);
        if (progress.dualStoryCompleted && !unlockedStoryIds.includes(finalChapter.id!)) {
          // 解锁顿悟时刻
          setShowFinalChapter(true);
          const savedProgress = JSON.parse(localStorage.getItem('gameProgress') || '{}');
          if (!savedProgress.unlockedStories) {
            savedProgress.unlockedStories = [];
          }
          if (!savedProgress.unlockedStories.includes(finalChapter.id!)) {
            savedProgress.unlockedStories.push(finalChapter.id!);
            localStorage.setItem('gameProgress', JSON.stringify(savedProgress));
            setUnlockedStoryIds([...savedProgress.unlockedStories]);
          }
        }
      }
    }
  }, [completedStoryIds, unlockedStories, finalChapter]);

  // 单人游戏逻辑
  const handleStartGame = async (storyOverride?: Story) => {
    const activeStory = storyOverride ?? selectedStory;
    if (!activeStory) return;

    if (storyOverride) {
      setSelectedStory(activeStory);
    }

    setIsStartingGame(true);
    setIsGameWon(false); // 重置游戏状态
    setAiReview('');
    setReviewRequested(false);
    goodStreakRef.current = 0;

    // 模拟游戏开始延迟
    setTimeout(() => {
      setGameStarted(true);
      setIsStartingGame(false);

      // 添加初始消息
      setGameMessages([
        {
          type: 'system',
          content: `游戏开始！故事：${activeStory.title}`,
          timestamp: new Date()
        },
        {
          type: 'story',
          content: activeStory.surface,
          timestamp: new Date()
        },
        {
          type: 'ai',
          content: '我已经知道了真相。你可以通过问是非题来推理答案。开始提问吧！',
          timestamp: new Date()
        }
      ]);
    }, 1000);
  };

  // 处理问题提交
  const handleQuestionSubmit = async () => {
    if (!currentQuestion.trim() || !selectedStory) return;

    // 添加玩家问题
    const playerMessage = {
      type: 'player',
      content: currentQuestion,
      timestamp: new Date()
    };

    setGameMessages(prev => [...prev, playerMessage]);

    // 添加AI思考中消息
    const thinkingMessage = {
      type: 'system',
      content: '🤔 AI正在思考...',
      timestamp: new Date()
    };
    setGameMessages(prev => [...prev, thinkingMessage]);

    try {
      // 调用真实的AI API
      const response = await fetch(getApiUrl('/api/ai/ask'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: currentQuestion,
          storyId: selectedStory.id,
          provider: 'qianwen'  // 使用通义千问
        })
      });

      const data = await response.json();

      // 移除思考中消息
      setGameMessages(prev => prev.filter(msg => msg !== thinkingMessage));

      // 标记提问质量，累计关键提问连击（连续3个奖励1次AI提示）
      const quality = data.success ? (data.data?.questionQuality || 'normal') : 'normal';
      setGameMessages(prev => prev.map(msg => msg === playerMessage ? { ...msg, quality } : msg));
      if (quality === 'good') {
        goodStreakRef.current += 1;
        if (goodStreakRef.current >= 3 && selectedStory.id && !bonusHintStories.includes(selectedStory.id)) {
          setBonusHintStories(prev => [...prev, selectedStory.id!]);
          goodStreakRef.current = 0;
          showToast('🎯 连续3个关键提问，奖励1次AI提示！', 'bg-purple-600');
        }
      } else {
        goodStreakRef.current = 0;
      }

      // 添加AI回答
      const aiMessage = {
        type: 'ai',
        content: data.success ? data.data.answer : '抱歉，AI服务暂时不可用',
        timestamp: new Date()
      };

      setGameMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI API调用失败:', error);

      // 移除思考中消息
      setGameMessages(prev => prev.filter(msg => msg !== thinkingMessage));

      // 错误时提供友好的错误信息
      const errorMessage = {
        type: 'ai',
        content: '抱歉，AI服务暂时不可用，请稍后再试',
        timestamp: new Date()
      };
      setGameMessages(prev => [...prev, errorMessage]);
    }

    setCurrentQuestion('');
  };

  // 处理答案猜测（AI判定）
  const handleGuess = async () => {
    if (!currentQuestion.trim() || !selectedStory) return;

    const guessText = currentQuestion.trim();
    const guessMessage = {
      type: 'player',
      content: `我猜答案是：${guessText}`,
      timestamp: new Date()
    };
    setGameMessages(prev => [...prev, guessMessage]);

    const thinkingMessage = {
      type: 'system',
      content: '🤖 AI正在判定答案...',
      timestamp: new Date()
    };
    setGameMessages(prev => [...prev, thinkingMessage]);

    try {
      const judgeQuestion = `玩家猜测答案：${guessText}。请仅基于当前题目的汤底真相判断是否一致；不要与其他题目混合判断。只要核心事实一致即可回答“是”，仅提到部分关键词或只说类别但缺少关键矛盾点，回答“不是”；无法判断则“不重要”。只回答“是/不是/不重要”。`;
      const response = await fetch(getApiUrl('/api/ai/ask'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: judgeQuestion,
          storyId: selectedStory.id,
          provider: 'qianwen',
          mode: 'guess'
        })
      });

      const data = await response.json();
      setGameMessages(prev => prev.filter(msg => msg !== thinkingMessage));

      if (!data.success) {
        setGameMessages(prev => [
          ...prev,
          { type: 'system', content: 'AI服务暂时不可用，无法判定，请稍后再试。', timestamp: new Date() }
        ]);
        return;
      }

      const isCorrect = data.data.answer === '是';
      const resultMessage = {
        type: 'system',
        content: isCorrect ? '🎉 恭喜你抵达真相！' : '❌ 答案不正确，继续加油！',
        timestamp: new Date()
      };
      setGameMessages(prev => [...prev, resultMessage]);

      if (isCorrect) {
        setIsGameWon(true);

        // 保存完成状态和解锁状态到本地存储
        const savedProgress = JSON.parse(localStorage.getItem('gameProgress') || '{}');
        if (!savedProgress.unlockedStories) {
          savedProgress.unlockedStories = [];
        }
        if (!savedProgress.completedStories) {
          savedProgress.completedStories = [];
        }

        // 标记为已完成
        if (!savedProgress.completedStories.includes(selectedStory.id!)) {
          savedProgress.completedStories.push(selectedStory.id!);
          setCompletedStoryIds([...savedProgress.completedStories]);
        }

        // 检查是否完成的是核心启示题目
        if (selectedStory.id === 'core_insight') {
          // 解锁后续主线剧情
          savedProgress.mainStoryUnlocked = true;
          setMainStoryUnlocked(true);
        }

        localStorage.setItem('gameProgress', JSON.stringify(savedProgress));

        // 已登录用户：写入游戏成绩（仪表盘/历史记录数据源）
        const user = authService.getCurrentUser();
        const token = localStorage.getItem('token');
        if (user && token) {
          fetch(getApiUrl('/api/user/record-game'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              storyId: selectedStory.id,
              storyTitle: selectedStory.title,
              userAnswer: guessText,
              mode: 'single',
              correct: true,
              questionsAsked: gameMessages.filter(m => m.type === 'player').length,
              hintsUsed: storyHintsUsed[selectedStory.id] || 0
            })
          }).catch(() => {});
        }

        // 每日挑战：今天完成的是每日题目则发放反诈徽章
        fetch(getApiUrl('/api/game/daily'))
          .then(r => r.json())
          .then(daily => {
            if (daily?.success && daily.data?.storyId === selectedStory.id) {
              const badges = JSON.parse(localStorage.getItem('lts_daily_badges') || '[]');
              const today = new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
              if (!badges.some((b: any) => b.date === today)) {
                badges.push({ date: today, storyId: selectedStory.id, title: selectedStory.title });
                localStorage.setItem('lts_daily_badges', JSON.stringify(badges));
                showToast('🏅 今日挑战完成，获得反诈徽章！', 'bg-amber-500');
              }
            }
          })
          .catch(() => {});

        goodStreakRef.current = 0;

        setTimeout(() => {
          const answerMessage = {
            type: 'answer',
            content: `正确答案：${selectedStory.answer}`,
            timestamp: new Date()
          };
          setGameMessages(prev => [...prev, answerMessage]);

          // 显示提示（如果有的话）
          if (selectedStory.hints && selectedStory.hints.length > 0) {
            const hintsMessage = {
              type: 'hints',
              content: `\ud83d\udca1 知识点：\n${selectedStory.hints.join('\n')}`,
              timestamp: new Date()
            };
            setGameMessages(prev => [...prev, hintsMessage]);
          }
        }, 1000);
      }
    } catch (error) {
      setGameMessages(prev => prev.filter(msg => msg !== thinkingMessage));
      setGameMessages(prev => [
        ...prev,
        { type: 'system', content: 'AI服务暂时不可用，无法判定，请稍后再试。', timestamp: new Date() }
      ]);
    } finally {
      setCurrentQuestion('');
    }
  };

  const handleGetHint = async () => {
    if (!selectedStory || isGettingHint) return;

    // 检查当前故事的提示限制（每题1次，关键提问连击达标可奖励1次）
    const storyId = selectedStory.id;
    const currentStoryHintsUsed = storyHintsUsed[storyId] || 0;
    const hintsPerStory = bonusHintStories.includes(storyId) ? 2 : 1;

    if (currentStoryHintsUsed >= hintsPerStory) {
      // 已经获取过提示，不再重复获取
      return;
    }

    setIsGettingHint(true);

    try {
      // 收集玩家已问过的问题
      const playerQuestions = gameMessages
        .filter(msg => msg.type === 'player')
        .map(msg => msg.content)
        .slice(-10); // 只取最近10个问题

      // 调用AI API获取提示
      const response = await fetch(getApiUrl('/api/ai/chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: `请为这个海龟汤题目提供提示`,
          context: 'turtle_soup_hint',
          storyId: selectedStory.id,
          hintCount: 1,  // 固定为1次提示
          playerQuestions: playerQuestions  // 传递玩家已问过的问题
        })
      });

      const data = await response.json();

      // 保存提示到历史记录
      const storyHintHistory = JSON.parse(localStorage.getItem('storyHintHistory') || '{}');
      if (!storyHintHistory[storyId]) {
        storyHintHistory[storyId] = [];
      }
      const hintContent = data.success ? data.response || '抱歉，暂无提示' : '抱歉，AI服务暂时不可用';
      storyHintHistory[storyId].push(hintContent);
      localStorage.setItem('storyHintHistory', JSON.stringify(storyHintHistory));

      // 更新当前故事的提示次数
      const updatedStoryHints = { ...storyHintsUsed, [storyId]: currentStoryHintsUsed + 1 };
      setStoryHintsUsed(updatedStoryHints);
      localStorage.setItem('storyHintsUsed', JSON.stringify(updatedStoryHints));

      // 触发界面重新渲染（更新提示框显示）
      setGameMessages(prev => [...prev]);

    } catch (error) {
      console.error('获取提示失败:', error);

      // 保存预设提示到历史记录
      const storyHintHistory = JSON.parse(localStorage.getItem('storyHintHistory') || '{}');
      if (!storyHintHistory[storyId]) {
        storyHintHistory[storyId] = [];
      }
      const fallbackHint = '仔细理解背景故事的描述，试着找出其中的关键点。';
      storyHintHistory[storyId].push(fallbackHint);
      localStorage.setItem('storyHintHistory', JSON.stringify(storyHintHistory));

      // 更新当前故事的提示次数
      const updatedStoryHints = { ...storyHintsUsed, [storyId]: currentStoryHintsUsed + 1 };
      setStoryHintsUsed(updatedStoryHints);
      localStorage.setItem('storyHintsUsed', JSON.stringify(updatedStoryHints));

      // 触发界面重新渲染
      setGameMessages(prev => [...prev]);
    } finally {
      setIsGettingHint(false);
    }
  };

  const handleLoginSuccess = () => {
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
    }
  };

  // 生成AI复盘报告
  const handleGetReview = async () => {
    if (!selectedStory || isGettingReview) return;
    setIsGettingReview(true);
    try {
      const playerQuestions = gameMessages
        .filter(msg => msg.type === 'player')
        .map(msg => String(msg.content))
        .filter(content => !content.startsWith('我猜答案是：'));
      const response = await fetch(getApiUrl('/api/ai/review'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storyId: selectedStory.id, history: playerQuestions, solved: true })
      });
      const data = await response.json();
      if (data.success) {
        setAiReview(data.data.review);
        setReviewRequested(true);
      } else {
        showToast('AI复盘暂时不可用，请稍后再试', 'bg-orange-500');
      }
    } catch (error) {
      console.error('获取AI复盘失败:', error);
      showToast('AI复盘暂时不可用，请稍后再试', 'bg-orange-500');
    } finally {
      setIsGettingReview(false);
    }
  };

  // 收藏本题知识卡片（localStorage 版本，可在「知识卡片」页查看）
  const handleSaveKnowledgeCard = (story: Story) => {
    if (!story.id) return;
    const cards = JSON.parse(localStorage.getItem('lts_knowledge_cards') || '[]');
    if (cards.some((c: any) => c.storyId === story.id)) return;
    cards.push({
      storyId: story.id,
      title: story.title,
      category: story.category,
      difficulty: story.difficulty,
      surface: story.surface,
      answer: story.answer,
      truth: (story as any).truth || '',
      hints: story.hints || [],
      tags: story.tags || [],
      savedAt: new Date().toISOString()
    });
    localStorage.setItem('lts_knowledge_cards', JSON.stringify(cards));
    setSavedCardIds(cards.map((c: any) => c.storyId));
    showToast('📁 知识卡片已收藏，可在「知识卡片」页查看', 'bg-emerald-600');
  };

  const handleResetProgress = () => {
    if (window.confirm('确定要重置所有游戏进度吗？将清空完成记录与提示使用记录（题目仍全部可选）。')) {
      // 重置语义：只清完成记录与提示记录；解锁状态保留（页面挂载时本就会全量开放普通题目）
      const savedProgress = JSON.parse(localStorage.getItem('gameProgress') || '{}');
      const resetProgress = {
        unlockedStories: savedProgress.unlockedStories || unlockedStories.map(s => s.id).filter((id): id is string => id !== undefined),
        completedStories: [],
        mainStoryUnlocked: false
      };
      localStorage.setItem('gameProgress', JSON.stringify(resetProgress));

      // 清空提示次数
      setStoryHintsUsed({});
      localStorage.removeItem('storyHintsUsed');

      // 清空提示历史
      localStorage.removeItem('storyHintHistory');
      // 清空双线进度（避免重置后误解锁最终章）
      localStorage.removeItem('dualStoryProgress');

      // 重置状态
      setUnlockedStoryIds(resetProgress.unlockedStories as string[]);
      setCompletedStoryIds([]);
      setSelectedStory(null);
      setGameStarted(false);
      setGameMessages([]);
      setIsGameWon(false);
      setShowFinalChapter(false);
      setMainStoryUnlocked(false);

      // 显示重置成功提示
      showToast('进度已重置：完成记录与提示记录已清空', 'bg-orange-600');
    }
  };

  // 跳过当前故事并切换到下一题
  const handleSkipStory = () => {
    if (!selectedStory) return;

    if (window.confirm('确定要跳过这个故事吗？这将切换到下一题。')) {
      const allStoryIds = unlockedStories.map(s => s.id);
      const currentIndex = allStoryIds.indexOf(selectedStory.id);
      const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % allStoryIds.length : 0;
      const nextStory = unlockedStories[nextIndex];

      if (!nextStory) {
        return;
      }

      setSelectedStory(nextStory);
      setGameStarted(false);
      setGameMessages([]);
      setIsGameWon(false);

      const toast = document.createElement('div');
      toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      toast.innerHTML = `<div class="flex items-center">已切换到下一题：${nextStory.title}</div>`;
      document.body.appendChild(toast);
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 2000);
    }
  };

  // 处理下一题
  const handleNextQuestion = () => {
    // 获取所有未完成且已解锁的故事（排除最终章）
    const availableStories = unlockedStories.filter(story =>
      story.id &&
      story.id !== 'final_chapter' &&  // 排除最终章
      unlockedStoryIds.includes(story.id) &&
      !completedStoryIds.includes(story.id)
    );

    if (availableStories.length > 0) {
      // 选择第一个可用的故事
      const nextStory = availableStories[0];
      setSelectedStory(nextStory);
      setGameStarted(false);
      setGameMessages([]);
      setIsGameWon(false);

      // 显示切换提示
      const toast = document.createElement('div');
      toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      toast.innerHTML = `<div class="flex items-center">正在切换到：${nextStory.title}</div>`;
      document.body.appendChild(toast);
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 2000);

      // 自动开始游戏
      setTimeout(() => {
        handleStartGame(nextStory);
      }, 500);
    } else {
      // 如果没有未完成的，显示提示并回到菜单
      const toast = document.createElement('div');
      toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      toast.innerHTML = '<div class="flex items-center">恭喜！你已完成所有题目</div>';
      document.body.appendChild(toast);
      setTimeout(() => {
        document.body.removeChild(toast);
        handleBackToMenu();
      }, 2000);
    }
  };

  // 处理返回菜单
  const handleBackToMenu = () => {
    setSelectedStory(null);
    setGameStarted(false);
    setGameMessages([]);
    setIsGameWon(false);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-ocean-50 to-white'}`}>
      {/* 导航栏 */}
      <header className={`shadow-sm border-b px-4 py-3 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              返回主页
            </button>
            <h1 className="text-xl font-bold flex items-center">
              <BookOpen className="w-6 h-6 mr-2 text-ocean-600" />
              海龟汤游戏
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleResetProgress}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-orange-600 transition"
              title="重置游戏进度"
            >
              <RotateCcw className="w-5 h-5 mr-1" />
              重置
            </button>
            {currentUser ? (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">欢迎, {currentUser.username}</span>
                <button
                  onClick={() => {
                    authService.logout();
                    setCurrentUser(null);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 transition"
                  title="退出登录"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
              >
                <LogIn className="w-4 h-4 mr-2" />
                登录
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* 最终章解锁提示 */}
        {showFinalChapter && finalChapter && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl p-6 text-center shadow-xl"
          >
            <h2 className="text-2xl font-bold mb-2">🌟 恭喜解锁最终章！</h2>
            <p className="text-lg">你已经完成了所有前置故事，现在可以挑战最终章了！</p>
          </motion.div>
        )}

        {/* 故事选择 */}
        {!selectedStory && !gameStarted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* 选项卡 */}
            <div className="flex justify-center mb-8">
              <div className={`rounded-lg p-1 inline-flex ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <button
                  onClick={() => setActiveTab('normal')}
                  className={`px-6 py-3 rounded-md font-medium transition ${
                    activeTab === 'normal'
                      ? 'bg-white text-ocean-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  普通案件
                </button>
                <button
                  onClick={() => setActiveTab('story')}
                  className={`px-6 py-3 rounded-md font-medium transition ${
                    activeTab === 'story'
                      ? 'bg-white text-pink-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  顿悟时刻
                  {!unlockedStoryIds.includes('final_chapter') && (
                    <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">待解锁</span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('insight')}
                  className={`px-6 py-3 rounded-md font-medium transition ${
                    activeTab === 'insight'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  主线剧情
                </button>
              </div>
            </div>

            <h2 className="text-3xl font-bold mb-6 text-gray-800">
              {activeTab === 'normal' && '选择故事开始游戏'}
              {activeTab === 'insight' && '主线剧情'}
              {activeTab === 'story' && '顿悟时刻'}
            </h2>

            {/* 进度条 */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  解决进度
                </span>
                <span className="text-sm font-medium text-gray-700">
                  {baseStoryCompletedIds.length} / {unlockedStories.length} ({progressPercentage}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-ocean-500 to-green-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
              {progressPercentage === 100 && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-green-600 font-bold mt-2"
                >
                  🎉 恭喜！你已经解密所有案件！
                </motion.p>
              )}
            </div>

            {/* 根据选项卡显示不同内容 */}
            {activeTab === 'normal' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* 显示普通故事 */}
                {unlockedStories.filter(story => story.id && unlockedStoryIds.includes(story.id) && story.id !== 'final_chapter').map((story, index) => {
                return (
                  <motion.div
                    key={story.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`rounded-xl shadow-lg p-6 hover:shadow-xl transition cursor-pointer ${
                      story.id && completedStoryIds.includes(story.id)
                        ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300'
                        : 'bg-white border-2 border-gray-200'
                    }`}
                    onClick={() => setSelectedStory(story)}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3 className={`text-xl font-bold ${
                        story.id && completedStoryIds.includes(story.id) ? 'text-green-800' : (isDark ? 'text-gray-100' : 'text-gray-800')
                      }`}>
                        {story.title}
                        {story.id && completedStoryIds.includes(story.id) && <span className="ml-2 text-green-600">✓</span>}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        story.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                        story.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {story.difficulty === 'easy' ? '简单' : story.difficulty === 'medium' ? '中等' : '困难'}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 line-clamp-2">{story.surface}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {story.category}
                      </span>
                      <button className="font-medium text-green-600 hover:text-green-700">
                        选择 →
                      </button>
                    </div>
                  </motion.div>
                );
              })}
              </div>
            )}

            {/* 顿悟时刻选项卡 */}
            {activeTab === 'story' && (
              <div className="text-center py-16">
                <div className="max-w-4xl mx-auto">
                  {/* 王大爷的剧情 - 顿悟时刻 */}
                  {finalChapter && unlockedStoryIds.includes(finalChapter.id!) ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{
                        scale: 1.02,
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                      }}
                      transition={{ duration: 0.3 }}
                      className={`rounded-xl shadow-lg p-8 hover:shadow-xl transition cursor-pointer mb-8 ${
                        finalChapter.id && completedStoryIds.includes(finalChapter.id)
                          ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300'
                          : 'bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-purple-400'
                      }`}
                      onClick={() => setSelectedStory(finalChapter)}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className={`text-2xl font-bold ${
                          finalChapter.id && completedStoryIds.includes(finalChapter.id) ? 'text-purple-800' : 'text-purple-800'
                        }`}>
                          🌟 {finalChapter.title}
                          {finalChapter.id && completedStoryIds.includes(finalChapter.id) && <span className="ml-2 text-purple-600">✓</span>}
                        </h3>
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-200 text-purple-800">
                          困难
                        </span>
                      </div>
                      <p className="text-gray-700 text-lg mb-6">{finalChapter.surface}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-purple-600 font-medium">
                          {finalChapter.category}
                        </span>
                        <button className="font-medium text-purple-600 hover:text-purple-700 text-lg">
                          {finalChapter.id && completedStoryIds.includes(finalChapter.id) ? '再次挑战 →' : '开始挑战 →'}
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-8 border-2 border-purple-200 mb-8">
                      <h3 className="text-3xl font-bold text-purple-800 mb-6">顿悟时刻</h3>
                      <div className="text-center py-8">
                        <span className="text-6xl">🔒</span>
                        <p className="text-gray-600 mt-4 text-lg">
                          完成双线叙事后解锁
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          请完成"现代篇"和"古代篇"的双线叙事故事
                        </p>
                      </div>
                    </div>
                  )}

                  {/* 核心启示题目 - 在完成王大爷剧情后解锁 */}
                  {finalChapter && finalChapter.id && completedStoryIds.includes(finalChapter.id) && insightStory && insightStory.id && (
                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-6">
                      <h4 className="font-bold text-yellow-800 mb-3 flex items-center">
                        <span className="text-2xl mr-2">💡</span>
                        核心启示：古今人性
                      </h4>
                      <p className="text-sm text-gray-700 mb-4">
                        理解古今诈骗手法的本质相似性
                      </p>
                      <button
                        onClick={() => setSelectedStory(insightStory)}
                        className={`w-full py-2 px-4 rounded-lg font-medium transition ${
                          insightStory.id && completedStoryIds.includes(insightStory.id)
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-yellow-600 text-white hover:bg-yellow-700'
                        }`}
                      >
                        {insightStory.id && completedStoryIds.includes(insightStory.id)
                          ? '重新挑战 →'
                          : '开始挑战 →'}
                      </button>
                    </div>
                  )}

                      {/* 解答核心启示后显示的启示 */}
                      {insightStory && insightStory.id && completedStoryIds.includes(insightStory.id) && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6 }}
                          className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-lg p-6 mt-6">
                          <h4 className="font-bold text-indigo-800 mb-4 flex items-center">
                            <span className="text-2xl mr-2">✨</span>
                            顿悟时刻的启示
                          </h4>
                          <div className="text-left space-y-4">
                            <p className="text-gray-700 text-lg">
                              经历双线叙事后，新警陆鸣深有感悟：古今诈骗手法虽形式各异，但利用的人性弱点却惊人相似。
                            </p>
                            <div className="grid md:grid-cols-3 gap-4">
                              <div className="bg-ocean-50 p-4 rounded-lg border border-ocean-200">
                                <h5 className="font-bold text-ocean-700 mb-2 flex items-center">
                                  <span className="w-3 h-3 bg-ocean-600 rounded-full mr-2"></span>
                                  核心洞察
                                </h5>
                                <p className="text-sm text-gray-700">
                                  贪婪、恐惧、孤独——这些永恒的人性弱点，正是诈骗者屡试不爽的突破口。
                                </p>
                              </div>
                              <div className="bg-warm-50 p-4 rounded-lg border border-warm-200">
                                <h5 className="font-bold text-warm-700 mb-2 flex items-center">
                                  <span className="w-3 h-3 bg-warm-600 rounded-full mr-2"></span>
                                  成长蜕变
                                </h5>
                                <p className="text-sm text-gray-700">
                                  从新手到熟手，陆鸣不仅学会了破案，更懂得了守护人心的责任。
                                </p>
                              </div>
                              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <h5 className="font-bold text-purple-700 mb-2 flex items-center">
                                  <span className="w-3 h-3 bg-purple-600 rounded-full mr-2"></span>
                                  经验传承
                                </h5>
                                <p className="text-sm text-gray-700">
                                  韩师傅的教导让陆鸣明白：守住本心，才能护他人平安。
                                </p>
                              </div>
                            </div>
                            <div className="bg-white p-5 rounded-lg border-2 border-indigo-300 shadow-inner mt-4">
                              <blockquote className="text-indigo-800 font-medium text-lg italic text-center">
                                "时代在变，手法在变，但人心不变。<br/>
                                作为警察，我们不仅要抓住罪犯，<br/>
                                更要守护好每一个可能受骗的心灵。"<br/>
                                <span className="text-sm text-gray-600 mt-2 block">——老韩对陆鸣的嘱托</span>
                              </blockquote>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* 后续主线剧情 */}
                      {mainStoryUnlocked && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6">
                          <h4 className="font-bold text-green-800 mb-3 flex items-center">
                            <span className="text-2xl mr-2">📖</span>
                            后续主线剧情
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            陆鸣的成长之路仍在继续...
                          </p>
                          <p className="text-xs text-gray-500 italic mb-4">
                            更多精彩故事，敬请期待
                          </p>
                          <div className="inline-block bg-gray-200 text-gray-600 px-4 py-2 rounded-full text-sm font-medium">
                            🚧 待创作开发
                          </div>
                        </div>
                      )}
                </div>
              </div>
            )}

            {/* 主线剧情选项卡 */}
            {activeTab === 'insight' && (
              <div className="text-center py-12">
                <div className="max-w-5xl mx-auto text-left">
                  <ScenarioHub />
                </div>
              </div>
            )}

            {/* 没有已解锁题目时的提示 */}
            {unlockedStories.filter(story => story.id && unlockedStoryIds.includes(story.id)).length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-10"
              >
                <BookOpen className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-4">还没有可用题目</p>
                <button
                  onClick={() => navigate('/stories')}
                  className="px-6 py-3 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition"
                >
                  前往题库总览
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* 游戏准备 */}
        {selectedStory && !gameStarted && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-3xl mx-auto"
          >
            <div className={`rounded-xl shadow-xl p-8 ${isDark ? 'bg-gray-800' : 'bg-white'} ${
              selectedStory.id === 'final_chapter' ? 'border-2 border-purple-400' : ''
            }`}>
              <div className="text-center mb-8">
                <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>已选择故事</h2>
                <div className="inline-block">
                  <div className={`${
                    selectedStory.id === 'final_chapter' 
                      ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300' 
                      : 'bg-ocean-50 border-2 border-ocean-200'
                  } rounded-lg p-6`}>
                    <h3 className={`text-2xl font-bold ${
                      selectedStory.id === 'final_chapter' ? 'text-purple-700' : 'text-ocean-700'
                    } mb-3`}>
                      {selectedStory.id === 'final_chapter' && '🌟 '}{selectedStory.title}
                    </h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      selectedStory.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                      selectedStory.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      selectedStory.id === 'final_chapter' ? 'bg-purple-200 text-purple-800' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {selectedStory.difficulty === 'easy' ? '简单' : 
                       selectedStory.difficulty === 'medium' ? '中等' : 
                       selectedStory.id === 'final_chapter' ? '最终章' : '困难'}
                    </span>
                    {selectedStory.tags && (
                      <div className="mt-2 flex gap-2 justify-center">
                        {selectedStory.tags.map((tag: string, index: number) => (
                          <span key={index} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={`rounded-lg p-6 mb-8 ${
                selectedStory.id === 'final_chapter' 
                  ? 'bg-gradient-to-r from-purple-50 to-pink-50' 
                  : 'bg-gray-50'
              }`}>
                <p className="text-gray-700 text-lg leading-relaxed">{selectedStory.surface}</p>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => handleStartGame()}
                  disabled={isStartingGame}
                  className={`px-8 py-4 rounded-lg hover:opacity-90 transition flex items-center text-lg font-semibold shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed ${
                    selectedStory.id === 'final_chapter'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'bg-ocean-600 text-white hover:bg-ocean-700'
                  }`}
                >
                  {isStartingGame ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      开始中...
                    </>
                  ) : (
                    <>
                      <Play className="w-6 h-6 mr-2" />
                      开始游戏
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSelectedStory(null);
                    setGameStarted(false);
                    setGameMessages([]);
                    setIsGameWon(false);
                  }}
                  className="bg-gray-200 text-gray-700 px-8 py-4 rounded-lg hover:bg-gray-300 transition text-lg font-semibold"
                >
                  重新选择
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* 单人游戏界面 */}
        {gameStarted && selectedStory && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className={`rounded-xl shadow-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} ${
              selectedStory.id === 'final_chapter' ? 'border-2 border-purple-400' : ''
            }`}>
              {/* 游戏头部 */}
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${
                  selectedStory.id === 'final_chapter' ? 'text-purple-300' : (isDark ? 'text-gray-100' : 'text-gray-800')
                }`}>
                  {selectedStory.id === 'final_chapter' && '🌟 '}{selectedStory.title}
                </h2>
                <button
                  onClick={() => {
                    setGameStarted(false);
                    setGameMessages([]);
                    setSelectedStory(null);
                    setIsGameWon(false);
                  }}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
              </div>

              {/* 故事背景 */}
              <div className={`border-l-4 p-4 mb-4 ${
                selectedStory.id === 'final_chapter'
                  ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-500'
                  : (isDark ? 'bg-gray-700 border-ocean-400' : 'bg-ocean-50 border-ocean-500')
              }`}>
                <p className="text-gray-700">{selectedStory.surface}</p>
              </div>

              {/* 提示框 */}
              <div className="mb-4">
                <div className={`border rounded-lg p-4 ${
                  storyHintsUsed[selectedStory.id] > 0
                    ? 'bg-purple-50 border-purple-300'
                    : (isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200')
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                      <Lightbulb className="w-4 h-4 mr-2" />
                      AI提示
                    </h3>
                    {storyHintsUsed[selectedStory.id] > 0 && (
                      <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded">
                        已获取
                      </span>
                    )}
                  </div>
                  {storyHintsUsed[selectedStory.id] > 0 ? (
                    <div className="text-sm text-gray-700">
                      {(() => {
                        const storyHintHistory = JSON.parse(localStorage.getItem('storyHintHistory') || '{}');
                        const hints = storyHintHistory[selectedStory.id] || [];
                        return hints.length > 0 ? hints[hints.length - 1] : '暂无提示';
                      })()}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      点击下方按钮获取AI提示
                    </div>
                  )}
                </div>
              </div>

              {/* 消息列表 */}
              <div className={`h-96 overflow-y-auto border rounded-lg p-4 mb-4 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                {gameMessages.map((msg, index) => (
                  <div
                    key={index}
                    className={`mb-3 ${msg.type === 'player' ? 'text-right' : ''}`}
                  >
                    <span
                      className={`inline-block px-4 py-2 rounded-lg ${
                        msg.type === 'system' ? 'bg-gray-200 text-gray-700' :
                        msg.type === 'player' ? 'bg-blue-500 text-white' :
                        msg.type === 'ai' ? 'bg-green-500 text-white' :
                        msg.type === 'story' ? 'bg-yellow-100 text-gray-800' :
                        msg.type === 'answer' ? 'bg-purple-500 text-white' :
                        msg.type === 'hints' ? 'bg-indigo-100 text-indigo-800 whitespace-pre-line' :
                        'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {msg.content}
                    </span>
                    {msg.type === 'player' && msg.quality && msg.quality !== 'guess' && (
                      <div className={`text-xs mt-0.5 ${
                        msg.quality === 'good' ? 'text-green-600' :
                        msg.quality === 'vague' ? 'text-orange-500' : 'text-gray-400'
                      }`}>
                        {msg.quality === 'good' ? '🎯 关键提问' : msg.quality === 'vague' ? '💭 试试更具体的是非题' : '普通提问'}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>

              {/* 游戏结束界面 */}
              {isGameWon && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-6 rounded-xl p-6 ${
                    selectedStory.id === 'final_chapter'
                      ? 'bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-400'
                      : 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300'
                  }`}
                >
                  <div className="text-center">
                    <h3 className={`text-2xl font-bold mb-4 ${
                      selectedStory.id === 'final_chapter' ? 'text-purple-800' :
                      selectedStory.id === 'core_insight' ? 'text-yellow-800' : 'text-green-800'
                    }`}>
                      {selectedStory.id === 'final_chapter' ? '🌟 梦醒时分！' :
                       selectedStory.id === 'core_insight' ? '🎊 顿悟时刻！' : '🎉 恭喜完成！'}
                    </h3>
                    <p className="text-gray-700 mb-4">
                      {selectedStory.id === 'final_chapter'
                        ? '你已经完成了新的篇章，梦境虽假，但警示心真...'
                        : selectedStory.id === 'core_insight'
                        ? '你已经领悟了核心启示：古今人性相通，骗术万变不离其宗！'
                        : '你已经成功解开了这个海龟汤的真相'}
                    </p>
                    {/* 显示剩余题目 */}
                    <div className="mb-6">
                      <p className="text-sm text-gray-600">
                        已完成：{baseStoryCompletedIds.length} / {unlockedStories.length} 题
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-purple-500 rounded-full transition-all duration-500"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </div>
                    {/* AI 复盘与知识卡片 */}
                    {selectedStory.id !== 'final_chapter' && selectedStory.id !== 'core_insight' && (
                      <div className="mt-2 mb-6 space-y-3 text-left">
                        {reviewRequested && aiReview ? (
                          <div className="bg-white border-2 border-indigo-200 rounded-lg p-4">
                            <h4 className="font-bold text-indigo-700 mb-2 flex items-center">
                              <Brain className="w-4 h-4 mr-1" /> AI 复盘报告
                            </h4>
                            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{aiReview}</p>
                          </div>
                        ) : (
                          <button
                            onClick={handleGetReview}
                            disabled={isGettingReview}
                            className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition flex items-center justify-center font-medium shadow disabled:opacity-60"
                          >
                            {isGettingReview ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                AI正在生成复盘报告...
                              </>
                            ) : (
                              <>
                                <Brain className="w-5 h-5 mr-2" />
                                生成 AI 复盘报告
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleSaveKnowledgeCard(selectedStory)}
                          disabled={savedCardIds.includes(selectedStory.id!)}
                          className="w-full py-2.5 bg-white border-2 border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 transition flex items-center justify-center font-medium disabled:opacity-60"
                        >
                          <BookmarkPlus className="w-5 h-5 mr-2" />
                          {savedCardIds.includes(selectedStory.id!) ? '已收藏知识卡片 ✓' : '收藏本题知识卡片'}
                        </button>
                      </div>
                    )}
                    {selectedStory.id === 'final_chapter' && (
                      <>
                        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                          <p className="text-purple-800 font-medium text-center mb-3">
                            🎊 双线叙事已完成！
                          </p>
                          <div className="text-sm text-purple-600 space-y-1">
                            <p>✓ 现代线：揭露电信诈骗真相</p>
                            <p>✓ 古代线：洞察古今诈骗手法</p>
                            <p className="text-center mt-2 italic text-gray-600">
                              "王大爷从梦中惊醒，发现自己还坐在派出所的椅子上。<br/>
                              原来，防范诈骗的意识已经深深地刻在了他的心里。<br/>
                              梦境虽假，但警惕心真。"
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                    {selectedStory.id === 'core_insight' && (
                      <>
                        <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-300">
                          <p className="text-yellow-800 font-medium text-center mb-3">
                            🎓 核心启示已完成！
                          </p>
                          <div className="text-sm text-gray-700 space-y-2">
                            <p>✓ 理解了古今诈骗手法的本质</p>
                            <p>✓ 洞察了人性的永恒弱点</p>
                            <p>✓ 掌握了守护心灵的钥匙</p>
                            <p className="text-center mt-3 font-bold text-green-700">
                              🎉 恭喜解锁后续主线剧情！
                            </p>
                            <p className="text-center mt-2 italic text-gray-600">
                              "陆鸣的成长之路仍在继续...<br/>
                              更多精彩故事，敬请期待"
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="flex gap-4 justify-center">
                      {selectedStory.id !== 'final_chapter' && selectedStory.id !== 'core_insight' ? (
                        <button
                          onClick={handleNextQuestion}
                          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center font-medium shadow-lg"
                        >
                          <ArrowRight className="w-5 h-5 mr-2" />
                          下一题
                        </button>
                      ) : selectedStory.id === 'final_chapter' ? (
                        <button
                          onClick={() => window.location.href = '/dual-story/dual-insight'}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition flex items-center font-medium shadow-lg"
                        >
                          <BookOpen className="w-5 h-5 mr-2" />
                          前往古今人性
                        </button>
                      ) : null}
                      <button
                        onClick={handleBackToMenu}
                        className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition flex items-center font-medium shadow-lg"
                      >
                        <Menu className="w-5 h-5 mr-2" />
                        菜单
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* 输入区域 */}
              {!isGameWon && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentQuestion}
                    onChange={(e) => setCurrentQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleQuestionSubmit()}
                    placeholder="输入你的问题（是非题）或答案猜测..."
                    className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`}
                  />
                  <button
                    onClick={handleQuestionSubmit}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    提问
                  </button>
                  <button
                    onClick={handleGuess}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                  >
                    猜答案
                  </button>
                  <button
                    onClick={handleGetHint}
                    disabled={isGettingHint || (storyHintsUsed[selectedStory?.id || ''] || 0) >= (selectedStory && bonusHintStories.includes(selectedStory.id) ? 2 : 1)}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    title="获取AI提示（每题限1次，连续3个关键提问奖励1次）"
                  >
                    {isGettingHint ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                        思考中
                      </>
                    ) : (storyHintsUsed[selectedStory?.id || ''] || 0) >= (selectedStory && bonusHintStories.includes(selectedStory.id) ? 2 : 1) ? (
                      <>
                        <Lightbulb className="w-4 h-4 mr-1" />
                        已提示
                      </>
                    ) : (
                      <>
                        <Lightbulb className="w-4 h-4 mr-1" />
                        获取提示
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleSkipStory}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                    title="跳过并切换下一题"
                  >
                    跳过
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </main>

      {/* 登录弹窗 */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
};

export default GamePage;
